/**
 * Resident name matching for duplicate detection on Add Resident
 * (docs/DUPLICATE_RESIDENT_DETECTION_PLAN.md §3).
 *
 * The backend runs the same rules in serverless/mysql_transaction.py
 * (residentNameKey / residentNameTier). Keep the two in step: the page checks
 * first against what it has loaded, and the Lambda re-checks against MySQL, so a
 * rule that only one side applies shows up as a dialog the other side then
 * contradicts.
 *
 * Stored names are not clean (about 9% carry leading or trailing spaces, and case
 * and inner spacing vary), so every comparison goes through nameKey.
 */

const SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv']);

/** trim, lower-case, drop "." and ",", collapse whitespace, drop a trailing suffix. */
export function normName(value) {
  const parts = String(value ?? '')
    .toLowerCase()
    .replace(/[.,]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length > 1 && SUFFIXES.has(parts[parts.length - 1])) parts.pop();
  return parts.join(' ');
}

/** Comparison key: normName without spaces, apostrophes or hyphens, so
    "O'Brien" = "OBrien", "Smith-Jones" = "Smith Jones", "Mary Ann" = "Maryann". */
export function nameKey(value) {
  return normName(value).replace(/[\s'-]/g, '');
}

/** Key of the first word only: "Mary Ann" and "Mary J" both give "mary". */
function firstTokenKey(value) {
  return nameKey(normName(value).split(' ')[0] || '');
}

/** Match tiers, strongest first. The label is what the dialog shows. */
export const MATCH_TIERS = {
  exact: { rank: 0, label: 'Same name' },
  firstToken: { rank: 1, label: 'Similar name' },
  nickname: { rank: 2, label: 'Matches nickname' },
  swapped: { rank: 3, label: 'Name reversed' },
};

/**
 * How `candidate` matches `other`, or null. Both are
 * { first, last, nickname }. A last name is required on both sides, so a bare
 * first name never matches on its own.
 */
export function nameMatchTier(candidate, other) {
  const f1 = nameKey(candidate.first);
  const l1 = nameKey(candidate.last);
  const f2 = nameKey(other.first);
  const l2 = nameKey(other.last);
  if (!f1 || !l1 || !f2 || !l2) return null;

  if (f1 === f2 && l1 === l2) return 'exact';
  if (l1 === l2 && firstTokenKey(candidate.first) === firstTokenKey(other.first)) return 'firstToken';
  const n1 = nameKey(candidate.nickname);
  const n2 = nameKey(other.nickname);
  if (l1 === l2 && ((n2 && f1 === n2) || (n1 && n1 === f2))) return 'nickname';
  if (f1 !== l1 && f1 === l2 && l1 === f2) return 'swapped';
  return null;
}

const ts = (value) => {
  const t = Date.parse(String(value || '').replace(' ', 'T'));
  return Number.isNaN(t) ? 0 : t;
};

/**
 * Residents (moved in and moved out) whose name matches the one being added.
 * Moved in first, then moved out; within each, strongest tier, then newest
 * move-in. Returns [{ resident, tier }].
 */
export function findResidentMatches(candidate, residents) {
  const out = [];
  (residents || []).forEach((r) => {
    const tier = nameMatchTier(candidate, {
      first: r.resident_first_name,
      last: r.resident_last_name,
      nickname: r.resident_nickname,
    });
    if (tier) out.push({ resident: r, tier });
  });
  return out.sort((a, b) =>
    (b.resident.is_active ? 1 : 0) - (a.resident.is_active ? 1 : 0)
    || MATCH_TIERS[a.tier].rank - MATCH_TIERS[b.tier].rank
    || ts(b.resident.move_in_date) - ts(a.resident.move_in_date));
}

/**
 * Pending EHR move-in tasks (getresidentstaged Content[0]) whose name matches.
 * Strongest tier, then most recently received. Returns [{ task, tier }].
 */
export function findTaskMatches(candidate, tasks) {
  const out = [];
  (tasks || []).forEach((t) => {
    const tier = nameMatchTier(candidate, {
      first: t.resident_first_name,
      last: t.resident_last_name,
      nickname: t.resident_nickname,
    });
    if (tier) out.push({ task: t, tier });
  });
  return out.sort((a, b) =>
    MATCH_TIERS[a.tier].rank - MATCH_TIERS[b.tier].rank
    || ts(b.task.create_time) - ts(a.task.create_time));
}
